import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type {
	ServiceOrderSource,
	ServiceOrderStatus,
} from '../types/service-order.types';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientEntity } from './client.entity';
import { ServiceOrderLineEntity } from './service-order-line.entity';
import { TripStopEntity } from './trip-stop.entity';
import { UserEntity } from './user.entity';

/**
 * PD-16797 — a service order: dated work for a technician at a customer
 * location, one line per thing to fix. It rides a service-trip stop the way a
 * sales order rides a delivery stop (`fk_trip_stop__id`).
 *
 * Kept apart from `order` on purpose: every reader of `order` treats it as a
 * sale (sales reports, goals, gross profit, ERP export, SM1, stop-completion
 * invoicing, the Replenishment and quick-order open-order rules).
 *
 * One service order spans every visit it takes. A visit that leaves lines
 * not done ends `partially_completed`, off the trip, so it is back on the
 * Schedule; the next trip it goes on works the remaining lines. Each line
 * records the stop that recorded its outcome, which keeps the visit history.
 *
 * Billing is a later step: lines may carry an item, quantity and price, so a
 * "Bill this service order" action can raise a normal order from the priced
 * lines (see `serviceOrderBillableLines` in libs/shared utils).
 *
 * SM1 exposure: none. New table; SM1 never names it.
 */
@Entity('service_order')
@Index('idx_service_order_client', ['clientId'])
@Index('idx_service_order_trip_stop', ['tripStopId'])
@Index('idx_service_order_status_date', ['status', 'scheduledDate'])
@Index('idx_service_order_sensor_alert', ['sensorAlertId'])
export class ServiceOrderEntity extends BaseEntity {
	@Column({ name: 'fk_client__id', type: 'int', unsigned: true })
	public clientId!: number;

	/** The location the work is at. NULL falls back to the ship-to address. */
	@Column({
		name: 'fk_client_address__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientAddressId!: number | null;

	/** Who to ask for on site. */
	@Column({
		name: 'fk_client_contact__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientContactId!: number | null;

	/** The day the work is wanted, a local calendar date ("YYYY-MM-DD"). */
	@Column({ name: 'scheduledDate', type: 'date' })
	public scheduledDate!: string;

	/** Optional time window on `scheduledDate` ("HH:MM:SS"). */
	@Column({ name: 'windowStart', type: 'time', nullable: true })
	public windowStart!: string | null;

	@Column({ name: 'windowEnd', type: 'time', nullable: true })
	public windowEnd!: string | null;

	/**
	 * The technician expected to do the work. Optional when the order is
	 * raised; the service trip's technician is what actually does it.
	 */
	@Column({
		name: 'fk_user__id_technician',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public technicianUserId!: number | null;

	@Column({
		name: 'source',
		type: 'varchar',
		length: 32,
		default: "'manual'",
	})
	public source!: ServiceOrderSource;

	/**
	 * The sensor fault this order was raised from (Module Faults → Schedule a
	 * fix). NULL for every other source.
	 */
	@Column({
		name: 'fk_sensor_alert__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public sensorAlertId!: number | null;

	@Column({
		name: 'status',
		type: 'varchar',
		length: 32,
		default: "'open'",
	})
	public status!: ServiceOrderStatus;

	/** The service-trip stop the order rides on; NULL while it waits. */
	@Column({
		name: 'fk_trip_stop__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public tripStopId!: number | null;

	/** Instructions for the technician ("call on arrival"). */
	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes!: string | null;

	/** When the last line was done (status `completed`). */
	@Column({ name: 'completedDate', type: 'datetime', nullable: true })
	public completedDate!: Date | null;

	@ManyToOne(() => ClientEntity)
	@JoinColumn({ name: 'fk_client__id' })
	public fkClient?: ClientEntity;

	@ManyToOne(() => ClientAddressEntity, { nullable: true })
	@JoinColumn({ name: 'fk_client_address__id' })
	public fkClientAddress?: ClientAddressEntity | null;

	@ManyToOne(() => UserEntity, { nullable: true })
	@JoinColumn({ name: 'fk_user__id_technician' })
	public technician?: UserEntity | null;

	@ManyToOne(() => TripStopEntity, { nullable: true })
	@JoinColumn({ name: 'fk_trip_stop__id' })
	public fkTripStop?: TripStopEntity | null;

	@OneToMany(() => ServiceOrderLineEntity, (line) => line.fkServiceOrder)
	public lines?: Array<ServiceOrderLineEntity>;
}
